import { forwardRef } from 'react';

import Button from 'components/field/Button';

import useMediaQuery from 'hooks/useMediaQuery';

import { Breakpoints } from 'utils/const.styles';

import { faXmark } from '@fortawesome/pro-solid-svg-icons';

import Image from './Image';
import Typography from './Typography';
import * as S from './styled/Alert.styled';

export const notificationIconsConfig = {
	success: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/notifications/success.svg',
	warning: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/notifications/warning.svg',
	error: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/notifications/error.svg',
	info: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/notifications/info.svg',
	update: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/notifications/update-notification.svg'
};

/**
 * @author @buunsal
 * @description Alert Component - Used to display a short, important message in a way that attracts the user's attention without interrupting the user's task.
 * @param {Object} props - props of the component.
 * @param {'info' | 'success' | 'warning' | 'error' | 'update'} [props.variant = 'info'] - Applies the ready to use styles.
 * @param {boolean} [props.showIcon = false] - If true, the component will show the icon.
 * @param {string} [props.header] - The header of the component.
 * @param {string} [props.content] - The content of the component.
 * @param {string} [props.ctaButtonLabel] - The label of the button.
 * @param {boolean} [props.ctaButtonLoading = false] - If true, the button will show a loading spinner.
 * @param {Function} [props.onCtaButtonClick] - The function to be called when the button is clicked.
 * @param {Function} [props.onClose] - The function to be called when the close button is clicked. If this prop is passed, the close button will be shown.
 * @param {string} [props.iconUrl] - The url of the icon to be shown.
 * @param {boolean} [props.compact = true] - If false the component will have more padding and a bigger icon.
 * @param {string} [props.className] - Adds classname to the component. Override or extend the styles applied to the component.
 * @param {Object} [props.style] - Adds style to the component. Override or extend the styles applied to the component.
 * @param {*} [props.children] - The content of the component.
 * @returns {JSX.Element}
 * @example
 * import Alert from 'components/global/Alert';
 *
 * <Alert variant="info">
 * 	<Typography>info</Typography>
 * </Alert>
 * <Alert variant="success">
 * 	<Typography>success</Typography>
 * </Alert>
 * <Alert variant="warning">
 * 	<Typography>warning</Typography>
 * </Alert>
 * <Alert variant="error">
 * 	<Typography>error</Typography>
 * </Alert>
 *
 * */
const Alert = (
	{
		variant = 'info',
		compact = true,
		showIcon,
		header,
		content,
		ctaButtonLabel,
		ctaButtonLoading = false,
		onCtaButtonClick,
		onClose,
		iconUrl,
		className,
		children,
		...moreProps
	},
	ref
) => {
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const webIconSize = !compact ? 60 : header ? 48 : 32;
	const mobileIconSize = !compact ? 44 : header ? 40 : 24;

	return (
		<S.Container
			ref={ref}
			className={`alert ${variant} ${!compact ? 'notification' : ''} ${className ?? ''} ${header ? 'bigger' : ''} ${onClose ? 'closeable' : ''}`}
			{...moreProps}>
			{showIcon && (
				<S.AlertIcon compact={compact} header={!!header}>
					<Image
						width={isMobile ? `${mobileIconSize}px` : `${webIconSize}px`}
						height={isMobile ? `${mobileIconSize}px` : `${webIconSize}px`}
						url={iconUrl ?? notificationIconsConfig[variant] ?? notificationIconsConfig.info}
					/>
				</S.AlertIcon>
			)}

			<S.Wrapper className={compact ? 'compact' : ''}>
				{(header || content) && (
					<S.TextContainer>
						{header && (
							<Typography variant="p2" fontWeight="500" noWrap>
								{header}
							</Typography>
						)}
						{content && (
							<Typography fontWeight="500" color="var(--alert-description)" noWrap>
								{content}
							</Typography>
						)}
					</S.TextContainer>
				)}

				{ctaButtonLabel && onCtaButtonClick && (
					<Button
						label={ctaButtonLabel}
						variant="transparent"
						onClick={onCtaButtonClick}
						className="alert-link"
						loading={ctaButtonLoading}
					/>
				)}

				{onClose && (
					<S.CloseButtonContainer className={compact ? 'compact' : ''}>
						<Button variant="borderless" icon={faXmark} iconButton onClick={onClose} />
					</S.CloseButtonContainer>
				)}
				{children}
			</S.Wrapper>
		</S.Container>
	);
};

export default forwardRef(Alert);
